import type { UserNotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import { LoopbackFilterLogicalOperatorEnum } from "@verbleif/lib/src/composables/Api/Type/LoopbackFilter/LoopbackFilterLogicalOperator.enum";
import { LoopbackFilterOperatorEnum } from "@verbleif/lib/src/composables/Api/Type/LoopbackFilter/LoopbackFilterOperator.enum";
import { provideSearchable } from "@verbleif/lib/src/composables/Query/searchable";
import { provideSortable } from "@verbleif/lib/src/composables/Query/sortable";
import { useHydraInfiniteCollection } from "@verbleif/lib/src/composables/Query/useHydraInfiniteCollection";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "vue-sonner";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { useServerErrorHandler } from "../../Error/serverErrorHandler";
import { useAppMutation, usePatchMutation } from "../../Mutation/useHydraMutations";
import { defineHydraSource } from "../../Query/defineHydraSource";
import { resourceKeys, useResourceScope } from "../../Query/resourceKeys";
import { useNotificationsV2 } from "../useNotificationsV2";
import {
  applyInboxScopeFilter,
  markAllInboxNotificationsReadMutation,
  markNotificationAsReadMutation,
  userNotificationsCollectionUrl,
} from "./notifications.api";
import { useUserNotificationsLive } from "./useUserNotificationsLive";

export function useInboxNotifications() {
  const authStore = useAuthStore();
  const { t } = useI18n();
  const scope = useResourceScope();
  const { isV2Enabled } = useNotificationsV2();
  const listEnabled = ref(false);
  const userIri = computed(() => authStore.user.value?.["@id"] ?? "");
  // The inbox belongs to the new notification module: without it there is no inbox to load or mark read.
  const ENDPOINT = computed(() => isV2Enabled.value && authStore.user.value?.id ? userNotificationsCollectionUrl(authStore.user.value.id) : "");
  const TRANSLATION_KEY = "settings.notifications.v2.inbox";
  const { onError } = useServerErrorHandler();
  const { refreshCountUnlessLive } = useUserNotificationsLive();
  const markAllReadMutation = useAppMutation(markAllInboxNotificationsReadMutation());
  const markOneReadMutation = useAppMutation(markNotificationAsReadMutation());
  const hideMutation = usePatchMutation<UserNotificationHydraItem, { active: false }>({
    mutationKey: ["notifications", "hide"],
  });

  const searchable = provideSearchable<UserNotificationHydraItem>([
    {
      name: "title",
      operator: LoopbackFilterOperatorEnum.ILIKE,
      logicalOperator: LoopbackFilterLogicalOperatorEnum.OR,
    },
    {
      name: "message",
      operator: LoopbackFilterOperatorEnum.LIKE,
      logicalOperator: LoopbackFilterLogicalOperatorEnum.OR,
    },
  ]);
  // Newest in the inbox first: an item held back by quiet hours is listed when it arrived, not when it was sent.
  const sortable = provideSortable({}, { field: "listedAt", direction: "DESC" });
  const source = computed(() => {
    if (!userIri.value || !ENDPOINT.value) {
      return null;
    }
    return defineHydraSource<UserNotificationHydraItem>(
      [...resourceKeys.notifications.list(scope.value), "of", userIri.value],
      ENDPOINT.value,
      (params) => {
        params.set("active", "true");
        applyInboxScopeFilter(params, scope.value);
      },
    );
  });
  const list = useHydraInfiniteCollection(source, {
    perPage: 20,
    searchable,
    sortable,
    enabled: computed(() => listEnabled.value && !!userIri.value && !!ENDPOINT.value),
  });

  const error = ref<Error | null>(null);
  const hasError = computed(() => error.value !== null || list.hasError.value);
  const hasNotifications = computed(() => list.items.value.length > 0);

  const unreadCount = computed(() => {
    return list.items.value.filter(item => !item.readAt).length;
  });

  const isAllRead = computed(() => unreadCount.value === 0);

  async function refetch() {
    listEnabled.value = true;
    if (!ENDPOINT.value) {
      return;
    }
    return list.refetch();
  }

  async function markAsRead() {
    if (!ENDPOINT.value || !hasNotifications.value || isAllRead.value || !authStore.user.value?.id) {
      return;
    }

    const previousReadAts = new Map<string, string | null>();
    for (const item of list.items.value) {
      previousReadAts.set(item["@id"], item.readAt);
    }
    const readAt = new Date().toISOString();
    for (const item of list.items.value) {
      if (!item.readAt) {
        list.patchItem(item["@id"], { readAt });
      }
    }

    try {
      error.value = null;
      await markAllReadMutation.mutateAsync({
        userId: authStore.user.value.id,
        scope: scope.value,
      });

      await Promise.all([refetch(), refreshCountUnlessLive()]);

      toast.success(t(`${TRANSLATION_KEY}.all_marked_read`));
    } catch (err) {
      for (const [iri, previous] of previousReadAts) {
        list.patchItem(iri, { readAt: previous });
      }
      error.value = err as Error;
      onError({ error: err });
      toast.error(t(`${TRANSLATION_KEY}.mark_read_error`));
    }
  }

  async function markOneAsRead(notification: UserNotificationHydraItem, hideToast = false) {
    if (!ENDPOINT.value || !notification.id || !notification["@id"] || notification.readAt) {
      return;
    }

    const previousReadAt = notification.readAt;
    const readAt = new Date().toISOString();
    list.patchItem(notification["@id"], { readAt });

    try {
      await markOneReadMutation.mutateAsync({
        notificationId: notification.id,
        readAt,
        isV2: true,
      });
      void refreshCountUnlessLive();
    } catch (caught) {
      list.patchItem(notification["@id"], { readAt: previousReadAt });
      onError({ error: caught, hideToast });
    }
  }

  /** Hides the item from the inbox (active false); an unread item no longer counts as unread either. */
  async function hide(notification: UserNotificationHydraItem) {
    if (!ENDPOINT.value || !notification["@id"]) {
      return;
    }

    list.removeItem(notification["@id"]);

    try {
      await hideMutation.mutateAsync({ url: notification["@id"], body: { active: false } });
      void refreshCountUnlessLive();
    } catch (caught) {
      // The removed row cannot be put back in place reliably: reload the list instead.
      void list.invalidate();
      onError({ error: caught });
    }
  }

  return {
    ...list,
    isAllRead,
    unreadCount,
    searchable,
    refetch,
    markAsRead,
    markOneAsRead,
    hide,
    error,
    hasError,
    hasNotifications,
  };
}
